import React, { useState, useEffect } from 'react';
import { useConfig } from '../context/ConfigContext';
import { useClients } from '../context/ClientsContext';
import { useUsers } from '../context/UsersContext';
import { C, S } from '../utils/styles';
import Icon from './Icon';
import StyledSelect from './StyledSelect';

export default function EmployerOverridesPanel() {
  const { config } = useConfig();
  const { activeClient, updateAnalysisConfig } = useClients();
  const { currentUser } = useUsers();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [customWaitingDays, setCustomWaitingDays] = useState('');
  
  const overrides = activeClient?.analysisConfig?.overrides || {};
  const isLocked = activeClient?.analysisConfig?.isLocked || false;
  const clientName = activeClient?.company?.legalName || activeClient?.company?.dba || '';
  
  // Check if any override differs from system defaults
  const systemDefaults = {
    waitingPeriod: config.employerPlanRules?.waiting_period_days || 60,
    minWeeklyHours: config.employerPlanRules?.minimum_weekly_hours || 20,
    eligibleClasses: config.employerPlanRules?.eligible_classes || {
      fulltime_w2: true,
      parttime_w2: true,
      seasonal_temp: false,
      union_cba: false,
    },
    localMinWage: config.employerPlanRules?.state_local_min_wage_override || 7.25,
    availablePlanTiers: (config.planTiers || []).filter(t => !t.archived).map(t => t.label),
  };
  
  // Get current values (override or system default)
  const currentValues = {
    waitingPeriod: overrides.waitingPeriod ?? systemDefaults.waitingPeriod,
    minWeeklyHours: overrides.minWeeklyHours ?? systemDefaults.minWeeklyHours,
    eligibleClasses: overrides.eligibleClasses || systemDefaults.eligibleClasses,
    localMinWage: overrides.localMinWage ?? systemDefaults.localMinWage,
    availablePlanTiers: overrides.availablePlanTiers || systemDefaults.availablePlanTiers,
  };
  
  const hasCustomOverrides = 
    overrides.waitingPeriod !== undefined && overrides.waitingPeriod !== systemDefaults.waitingPeriod ||
    overrides.minWeeklyHours !== undefined && overrides.minWeeklyHours !== systemDefaults.minWeeklyHours ||
    overrides.localMinWage !== undefined && overrides.localMinWage !== systemDefaults.localMinWage ||
    overrides.eligibleClasses && JSON.stringify(overrides.eligibleClasses) !== JSON.stringify(systemDefaults.eligibleClasses) ||
    overrides.availablePlanTiers && JSON.stringify(overrides.availablePlanTiers) !== JSON.stringify(systemDefaults.availablePlanTiers);
  
  // Generate collapsed summary
  const getCollapsedSummary = () => {
    if (!activeClient) return 'No client selected';
    
    const name = clientName || 'Unnamed Client';
    
    if (!hasCustomOverrides) {
      return `${name}: Default rules (${currentValues.waitingPeriod}-day wait, ${currentValues.minWeeklyHours} hrs min, FT+PT)`;
    }
    
    const waitText = currentValues.waitingPeriod === 0 ? 'waived wait' : `${currentValues.waitingPeriod}-day wait`;
    const hoursText = `${currentValues.minWeeklyHours} hrs min`;
    
    const classCount = Object.values(currentValues.eligibleClasses || {}).filter(Boolean).length;
    const tierCount = currentValues.availablePlanTiers?.length || 0;
    const totalTiers = (config.planTiers || []).filter(t => !t.archived).length;
    
    let classText = 'FT';
    if (currentValues.eligibleClasses?.fulltime_w2 && currentValues.eligibleClasses?.parttime_w2) {
      classText = 'FT+PT';
      if (currentValues.eligibleClasses?.seasonal_temp) classText += '+Seasonal';
      if (currentValues.eligibleClasses?.union_cba) classText += '+Union';
    } else if (currentValues.eligibleClasses?.parttime_w2) {
      classText = 'PT';
    }
    
    const tierText = tierCount === totalTiers ? '' : `, ${tierCount} of ${totalTiers} plans`;
    
    return `${name}: Custom (${waitText}, ${hoursText}, ${classText}${tierText})`;
  };
  
  const handleWaitingPeriodChange = (value) => {
    if (isLocked) return;
    if (value === 'custom') {
      setCustomWaitingDays('');
    } else {
      updateAnalysisConfig(activeClient.id, {
        overrides: { ...overrides, waitingPeriod: parseInt(value) }
      }, currentUser?.id);
      setCustomWaitingDays('');
    }
  };
  
  const handleCustomWaitingDays = (value) => {
    if (isLocked) return;
    setCustomWaitingDays(value);
    const num = parseInt(value);
    if (!isNaN(num) && num >= 0) {
      updateAnalysisConfig(activeClient.id, {
        overrides: { ...overrides, waitingPeriod: num }
      }, currentUser?.id);
    }
  };
  
  const handleClassToggle = (classKey) => {
    if (isLocked) return;
    updateAnalysisConfig(activeClient.id, {
      overrides: {
        ...overrides,
        eligibleClasses: {
          ...overrides.eligibleClasses,
          [classKey]: !overrides.eligibleClasses?.[classKey],
        }
      }
    }, currentUser?.id);
  };
  
  const handlePlanTierToggle = (tierLabel) => {
    if (isLocked) return;
    // Start with current overrides, or fall back to system defaults if no overrides exist yet
    const current = overrides.availablePlanTiers || systemDefaults.availablePlanTiers;
    const updated = current.includes(tierLabel)
      ? current.filter(t => t !== tierLabel)
      : [...current, tierLabel];
    updateAnalysisConfig(activeClient.id, {
      overrides: { ...overrides, availablePlanTiers: updated }
    }, currentUser?.id);
  };
  
  const handleResetToDefaults = () => {
    if (isLocked) return;
    if (window.confirm('Reset all overrides to system defaults?')) {
      updateAnalysisConfig(activeClient.id, {
        overrides: {
          waitingPeriod: systemDefaults.waitingPeriod,
          minWeeklyHours: systemDefaults.minWeeklyHours,
          eligibleClasses: { ...systemDefaults.eligibleClasses },
          localMinWage: systemDefaults.localMinWage,
          availablePlanTiers: [...systemDefaults.availablePlanTiers],
        }
      }, currentUser?.id);
    }
  };
  
  const applyPreset = (presetName) => {
    if (isLocked) return;
    const presets = {
      maximum: {
        waitingPeriod: 0,
        minWeeklyHours: 0,
        eligibleClasses: {
          fulltime_w2: true,
          parttime_w2: true,
          seasonal_temp: true,
          union_cba: true,
        },
        availablePlanTiers: ['Plan 1500', 'Plan 1200', 'Plan 1050', 'Plan 900', 'Plan 750', 'Plan 600'],
      },
      conservative: {
        waitingPeriod: 60,
        minWeeklyHours: 30,
        eligibleClasses: {
          fulltime_w2: true,
          parttime_w2: true,
          seasonal_temp: false,
          union_cba: false,
        },
        availablePlanTiers: ['Plan 1200', 'Plan 900', 'Plan 750', 'Plan 600'],
      },
      standard: {
        waitingPeriod: 30,
        minWeeklyHours: 20,
        eligibleClasses: {
          fulltime_w2: true,
          parttime_w2: true,
          seasonal_temp: false,
          union_cba: false,
        },
        availablePlanTiers: ['Plan 1200', 'Plan 900', 'Plan 750', 'Plan 600'],
      },
    };
    
    const preset = presets[presetName];
    if (preset) {
      updateAnalysisConfig(activeClient.id, {
        overrides: { ...overrides, ...preset }
      }, currentUser?.id);
    }
  };
  
  const waitingPeriodOptions = [0, 30, 60, 90];
  const isCustomWaiting = !waitingPeriodOptions.includes(overrides.waitingPeriod);
  
  if (!activeClient) {
    return null; // Don't show if no client selected
  }
  
  return (
    <div style={{ marginBottom: 16 }}>
      {/* Collapsed View */}
      {!isExpanded && (
        <div 
          onClick={() => !isLocked && setIsExpanded(true)}
          style={{
            ...S.card,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: isLocked ? 'not-allowed' : 'pointer',
            background: isLocked ? C.bg : (hasCustomOverrides ? C.accentLight : C.card),
            border: `1px solid ${isLocked ? C.red : (hasCustomOverrides ? C.accent : C.border)}`,
            transition: 'all 0.2s ease',
            opacity: isLocked ? 0.7 : 1,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {isLocked && <Icon name="lock-closed" style={{ fontSize: 16, color: C.red }} />}
            <Icon name="business-outline" style={{ fontSize: 18, color: isLocked ? C.red : (hasCustomOverrides ? C.accent : C.textMid) }} />
            <span style={{ fontSize: 13, color: isLocked ? C.red : (hasCustomOverrides ? C.accent : C.text), fontWeight: hasCustomOverrides ? 600 : 500 }}>
              {getCollapsedSummary()}
            </span>
            {isLocked && (
              <span style={{ fontSize: 10, color: C.red, background: C.redLight, padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>
                LOCKED
              </span>
            )}
          </div>
          {!isLocked && (
            <button
              style={{
                padding: '6px 12px',
                background: C.accent,
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Icon name="chevron-down" style={{ fontSize: 12 }} />
              Customize
            </button>
          )}
        </div>
      )}
      
      {/* Expanded View */}
      {isExpanded && (
        <div style={{ ...S.card, padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {isLocked && <Icon name="lock-closed" style={{ fontSize: 18, color: C.red }} />}
              <Icon name="business-outline" style={{ fontSize: 20, color: isLocked ? C.red : C.accent }} />
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.text }}>
                Employer Overrides
              </h3>
              {isLocked && (
                <span style={{ fontSize: 10, color: C.red, background: C.redLight, padding: '3px 8px', borderRadius: 4, fontWeight: 700 }}>
                  LOCKED
                </span>
              )}
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              style={{
                padding: '6px 12px',
                background: 'transparent',
                color: C.textMid,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Icon name="chevron-up" style={{ fontSize: 12 }} />
              Collapse
            </button>
          </div>
          
          {isLocked && (
            <div style={{ 
              marginBottom: 16, 
              padding: '10px 14px', 
              background: C.redLight, 
              border: `1px solid ${C.red}`,
              borderRadius: 6,
              fontSize: 12,
              color: C.red,
              fontWeight: 600,
            }}>
              🔒 Settings are locked. Go to client drawer to unlock.
            </div>
          )}
          
          {/* Quick Presets */}
          <div style={{ marginBottom: 16, opacity: isLocked ? 0.5 : 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 10, textTransform: 'uppercase' }}>
              Quick Presets
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => applyPreset('maximum')}
                disabled={isLocked}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  background: C.bg,
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => !isLocked && (e.currentTarget.style.borderColor = C.accent)}
                onMouseLeave={(e) => !isLocked && (e.currentTarget.style.borderColor = C.border)}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                  Maximum Eligibility
                </div>
                <div style={{ fontSize: 10, color: C.textMid, lineHeight: 1.4 }}>
                  Widest net: include as many employees as legally possible
                </div>
              </button>
              <button
                onClick={() => applyPreset('conservative')}
                disabled={isLocked}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  background: C.bg,
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => !isLocked && (e.currentTarget.style.borderColor = C.accent)}
                onMouseLeave={(e) => !isLocked && (e.currentTarget.style.borderColor = C.border)}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                  Conservative
                </div>
                <div style={{ fontSize: 10, color: C.textMid, lineHeight: 1.4 }}>
                  Industry standard: FT/PT only, 60-day wait, 30 hrs/week
                </div>
              </button>
              <button
                onClick={() => applyPreset('standard')}
                disabled={isLocked}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  background: C.bg,
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => !isLocked && (e.currentTarget.style.borderColor = C.accent)}
                onMouseLeave={(e) => !isLocked && (e.currentTarget.style.borderColor = C.border)}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                  Standard
                </div>
                <div style={{ fontSize: 10, color: C.textMid, lineHeight: 1.4 }}>
                  Balanced approach: FT/PT, 30-day wait, 20 hrs/week
                </div>
              </button>
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, opacity: isLocked ? 0.6 : 1 }}>
            {/* Left Column */}
            <div>
              {/* Waiting Period */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ ...S.label, marginBottom: 6 }}>Waiting Period</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <StyledSelect
                    value={isCustomWaiting ? 'custom' : String(currentValues.waitingPeriod)}
                    onValueChange={handleWaitingPeriodChange}
                    disabled={isLocked}
                    options={[
                      { value: "0", label: "Waived (0 days)" },
                      { value: "30", label: "30 days" },
                      { value: "60", label: "60 days" },
                      { value: "90", label: "90 days" },
                      { value: "custom", label: "Custom..." }
                    ]}
                    style={{ fontSize: 12, flex: 1 }}
                  />
                  {isCustomWaiting && (
                    <input
                      type="number"
                      value={customWaitingDays || currentValues.waitingPeriod}
                      onChange={(e) => handleCustomWaitingDays(e.target.value)}
                      disabled={isLocked}
                      placeholder="Days"
                      style={{ ...S.input, fontSize: 12, width: 80, cursor: isLocked ? 'not-allowed' : 'text' }}
                      min="0"
                    />
                  )}
                </div>
              </div>
              
              {/* Min Weekly Hours */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ ...S.label, marginBottom: 6 }}>Minimum Weekly Hours</label>
                <input
                  type="number"
                  value={currentValues.minWeeklyHours}
                  onChange={(e) => {
                    if (!isLocked) {
                      updateAnalysisConfig(activeClient.id, {
                        overrides: { ...overrides, minWeeklyHours: parseFloat(e.target.value) || 0 }
                      }, currentUser?.id);
                    }
                  }}
                  disabled={isLocked}
                  style={{ ...S.input, fontSize: 12, cursor: isLocked ? 'not-allowed' : 'text' }}
                  min="0"
                  max="40"
                  step="1"
                />
              </div>
              
              {/* Local Min Wage */}
              <div>
                <label style={{ ...S.label, marginBottom: 6 }}>Local Minimum Wage ($/hour)</label>
                <input
                  type="number"
                  value={currentValues.localMinWage}
                  onChange={(e) => {
                    if (!isLocked) {
                      updateAnalysisConfig(activeClient.id, {
                        overrides: { ...overrides, localMinWage: parseFloat(e.target.value) || 7.25 }
                      }, currentUser?.id);
                    }
                  }}
                  disabled={isLocked}
                  style={{ ...S.input, fontSize: 12, cursor: isLocked ? 'not-allowed' : 'text' }}
                  min="0"
                  step="0.25"
                />
              </div>
            </div>
            
            {/* Right Column */}
            <div>
              {/* Eligible Classes */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ ...S.label, marginBottom: 6 }}>Eligible Employment Classes</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: isLocked ? 'not-allowed' : 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={currentValues.eligibleClasses?.fulltime_w2 !== false}
                      onChange={() => handleClassToggle('fulltime_w2')}
                      disabled={isLocked}
                      style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                    />
                    Full-Time W-2
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: isLocked ? 'not-allowed' : 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={currentValues.eligibleClasses?.parttime_w2 !== false}
                      onChange={() => handleClassToggle('parttime_w2')}
                      disabled={isLocked}
                      style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                    />
                    Part-Time W-2
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: isLocked ? 'not-allowed' : 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={overrides.eligibleClasses?.seasonal_temp || false}
                      onChange={() => handleClassToggle('seasonal_temp')}
                      disabled={isLocked}
                      style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                    />
                    Seasonal/Temporary Employees
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: isLocked ? 'not-allowed' : 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={currentValues.eligibleClasses?.union_cba || false}
                      onChange={() => handleClassToggle('union_cba')}
                      disabled={isLocked}
                      style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                    />
                    Union Employees (CBA-covered)
                  </label>
                </div>
              </div>
              
              {/* Available Plan Tiers */}
              <div>
                <label style={{ ...S.label, marginBottom: 6 }}>
                  Available Plan Tiers <span style={{ fontSize: 10, color: C.textLight }}>(uncheck to exclude)</span>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  {(config.planTiers || []).filter(tier => !tier.archived).map((tier) => (
                    <label 
                      key={tier.label} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 6, 
                        fontSize: 11, 
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                        padding: '6px 8px',
                        background: (overrides.availablePlanTiers || []).includes(tier.label) ? C.greenLight : C.bg,
                        borderRadius: 6,
                        border: `1px solid ${(overrides.availablePlanTiers || []).includes(tier.label) ? C.green : C.border}`,
                        opacity: isLocked ? 0.6 : 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={currentValues.availablePlanTiers.includes(tier.label)}
                        onChange={() => handlePlanTierToggle(tier.label)}
                        disabled={isLocked}
                        style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                      />
                      {tier.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.border}`, display: 'flex', gap: 10 }}>
            <button
              onClick={handleResetToDefaults}
              disabled={isLocked}
              style={{
                padding: '8px 14px',
                background: 'transparent',
                color: isLocked ? C.textLight : C.textMid,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: isLocked ? 'not-allowed' : 'pointer',
                opacity: isLocked ? 0.5 : 1,
              }}
            >
              Reset to System Defaults
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
